<template>
  <header class="page-header">
    <div class="grow">
      <!-- The way back up, for a page that lives inside another (a customer's
           file, a quote, an order). It belongs here and not in each page's
           action buttons: "back" is navigation, reads at the top-left where
           every application puts it, and a nested page without it forces the
           sidebar on people for a one-level hop. -->
      <RouterLink v-if="backHref" :to="backHref" class="page-header__back">
        <span aria-hidden="true">←</span> {{ backText }}
      </RouterLink>
      <h1 class="page-header__title">
        {{ title }}
        <!--
          The screen's own explanation, on and off. Beside the title because
          that is where somebody looks when they do not know where they are —
          not in the actions, which is where somebody looks when they do.
        -->
        <button
          v-if="guide"
          type="button"
          class="page-header__guide"
          :class="{ 'page-header__guide--on': guideOpen }"
          :aria-label="guideOpen ? uiLabel('guideHide') : uiLabel('guideShow')"
          :aria-pressed="guideOpen"
          @click="toggleGuide"
        >
          <span aria-hidden="true">?</span>
        </button>

        <!--
          El tutorial de esta función, en OTRA PESTAÑA.

          Otra pestaña y no un diálogo, y esa es toda la diferencia: quien está
          aprendiendo necesita leer el tutorial MIENTRAS toca la pantalla, no
          antes de poder tocarla. Un modal tapa justo aquello que explica y
          obliga a memorizar el paso para cerrarlo y repetirlo.

          Distinto del «?» de arriba: aquél enciende las notas de esta pantalla,
          sobre las cosas que describen; éste abre la explicación entera de cómo
          funciona la función. Una pantalla puede tener los dos, y casi ninguna
          tiene ambos.

          Solo si esta persona quiere tutoriales (ver `useTutorials`): quien ya
          se sabe la casa lo apaga en su perfil y no vuelve a verlo.
        -->
        <!--
          Con las dos ayudas en la misma pantalla, UN solo «?». Dos círculos
          iguales pegados al título se leían como un duplicado —lo dijo el primer
          cliente que lo vio en la ficha de una obra—, y nadie adivina que uno
          enciende notas y el otro abre un manual. El «?» enciende las notas, y
          con ellas encendidas aparece al lado el enlace al manual, con su nombre:
          quien ya está pidiendo explicaciones es quien quiere la entera.
        -->
        <a
          v-if="tutorial && tutorialsOn && (!guide || guideOpen)"
          :class="guide ? 'page-header__manual' : 'page-header__guide'"
          :href="tutorial"
          target="_blank"
          rel="noopener"
          :aria-label="uiLabel('tutorialOpen')"
          :title="uiLabel('tutorialOpen')"
        >
          <span v-if="guide">{{ uiLabel('tutorialShort') }} ↗</span>
          <span v-else aria-hidden="true">?</span>
        </a>
      </h1>
      <p v-if="subtitle" class="page-header__subtitle">{{ subtitle }}</p>
    </div>
    <div
      v-if="$slots.default"
      class="page-header__actions row gap-2 wrap"
      :class="{ 'page-header__actions--docked': stickyActions }"
    ><slot /></div>
  </header>
</template>

<script setup>
import { computed, onUnmounted, watch, watchEffect } from 'vue'

import { useGuide } from '../composables/useGuide'
import { tutorialsOn } from '../composables/useTutorials'
import { currentPageTitle, useReturnTo } from '../composables/useReturnTo'
import { uiLabel } from '../utils/labels'

const props = defineProps({
  title: { type: String, required: true },
  subtitle: String,
  // Where "back" leads, and what to call it ("Clientes"). Route location or
  // path; rendered only when given, so flat pages change nothing.
  backTo: { type: [String, Object], default: null },
  backLabel: { type: String, default: '' },
  // The id of this screen's guide, e.g. 'quotes.catalog'. Given it, the header
  // shows the "?" and the page's <UiGuideNote> captions become switchable.
  // A page that declares no guide is exactly as it was.
  guide: { type: String, default: null },
  // La dirección del tutorial de esta función, p. ej.
  // '/work-orders/templates/help'. Dada, la cabecera ofrece un «?» que la abre
  // EN OTRA PESTAÑA — y solo a quien tenga los tutoriales encendidos en su
  // perfil. Una pantalla que no declara ninguno queda exactamente como estaba.
  tutorial: { type: String, default: null },
  /**
   * En el teléfono, las acciones se anclan abajo en vez de quedarse arriba.
   *
   * Arriba se van con el scroll: en una ficha larga, «Guardar» y «Eliminar»
   * dejan de existir en cuanto empiezas a leer, y hay que subir del todo para
   * encontrarlos. Abajo están siempre, donde ya está el pulgar, y en el mismo
   * sitio en todas las pantallas que lo pidan — que es lo que hace que no haya
   * que buscarlos en cada una.
   *
   * Es una decisión de cada pantalla y no del componente: una lista con un solo
   * «Nuevo» no gana nada con una barra fija, y una ficha con tres acciones sí.
   * Sin la propiedad, la cabecera se comporta exactamente como antes.
   */
  stickyActions: { type: Boolean, default: false },
})

// El sitio que la barra ocupa, reservado en el cuerpo mientras esta pantalla
// esté montada: si no, lo último de la página se queda debajo de la barra y no
// hay forma de llegar a ello.
const DOCKED_CLASS = 'ui-docked-actions'

watchEffect((onCleanup) => {
  if (!props.stickyActions) return
  document.body.classList.add(DOCKED_CLASS)
  onCleanup(() => document.body.classList.remove(DOCKED_CLASS))
})

const { open: guideOpen, toggle: toggleGuide, close } = useGuide(props.guide ?? '')

// The guide belongs to this screen, so it leaves with it: coming back to a page
// finds it closed, which is the only state it has by default.
onUnmounted(close)

/**
 * El «volver», que no siempre es el de esta pantalla.
 *
 * `backTo` es la vuelta de siempre —de una factura, a Facturas— y es la correcta
 * cuando se llegó por la lista. Cuando se llegó desde OTRA ficha, quien navegó
 * dejó dicho de dónde venía, y volver al listado sería perder el cliente en el
 * que se estaba trabajando, la pestaña abierta y el sitio de la lista.
 *
 * Gana el rastro, y su etiqueta con él: el enlace dice el nombre de la ficha de
 * la que se vino, que es lo que la persona reconoce.
 */
const cameFrom = useReturnTo()

const backHref = computed(() => cameFrom.value?.to ?? props.backTo)
const backText = computed(() => (cameFrom.value ? cameFrom.value.label || props.backLabel : props.backLabel))

/**
 * Y al revés: esta pantalla dice cómo se llama, para que la SIGUIENTE pueda
 * ofrecer la vuelta con su nombre. Es el único sitio del ERP que conoce el
 * título de la pantalla que se está mirando.
 */
watch(() => props.title, (title) => { currentPageTitle.value = title ?? '' }, { immediate: true })

onUnmounted(() => {
  // Sólo si sigue siendo el suyo: al navegar, la pantalla nueva monta su
  // cabecera ANTES de que se desmonte la vieja, y limpiar a ciegas borraría el
  // título que la nueva acaba de escribir.
  if (currentPageTitle.value === props.title) currentPageTitle.value = ''
})
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  flex-wrap: wrap;
  margin-bottom: var(--space-6);
}
.page-header__back {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
  color: var(--color-text-soft);
  text-decoration: none;
  margin-bottom: var(--space-2);
}
.page-header__back:hover { color: var(--color-text); }

/* ── El «volver», con un dedo ─────────────────────────────────────────────────
   Medía 60×20 en un móvil, y es la única salida de una ficha que se abrió desde
   una lista: quien falla el toque se queda dentro y acaba usando el gesto del
   navegador, que en una SPA no siempre vuelve donde uno cree.

   Se agranda con relleno y un margen negativo que lo compensa, no con `height`:
   así el objetivo crece hasta 44 px sin mover ni un píxel del título que va
   debajo. `pointer: coarse` y no un ancho de pantalla, porque lo que decide es
   con qué se toca y no cuánto mide la ventana — una tableta con teclado tiene
   pantalla de sobra y se sigue tocando con el dedo. */
@media (pointer: coarse) {
  .page-header__back {
    /* Con `backLabel` vacío queda solo la flecha, y el objetivo medía 12 px de
       ANCHO: alto de sobra y nada donde acertar. El mínimo aplica a los dos
       lados. Con etiqueta no cambia nada, porque ya mide más. */
    min-width: 44px;
    justify-content: center;
    min-height: 44px;
    padding-block: var(--space-2);
    margin-block: calc(var(--space-2) * -1) var(--space-1);
    font-size: var(--text-md);
  }
}

.page-header__title { font-size: var(--text-2xl); }

/* Small, quiet, and the same size as the text it sits beside — it has to be
   findable without competing with the title for attention. */
.page-header__guide {
  /* inline-flex: cuando es un enlace (`<a>`, solo manual) un elemento en línea
     no toma ancho ni alto, y el «?» medía 8 × 19 en un teléfono. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  width: 20px;
  height: 20px;
  margin-left: var(--space-2);
  padding: 0;
  vertical-align: middle;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: none;
  color: var(--color-text-mute);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  transition: color var(--fast), border-color var(--fast), background var(--fast);
}
.page-header__guide:hover,
.page-header__guide:focus-visible {
  color: var(--color-text);
  border-color: var(--color-text-mute);
}
/* On, and saying so: the notes on screen came from here and this is what turns
   them off again. Neutral rather than the brand colour, which is whatever a
   client's happens to be and can land unreadable on the dark theme. */
.page-header__guide--on {
  background: var(--color-gray-100);
  border-color: var(--color-text-mute);
  color: var(--color-text);
}

/* El enlace al manual cuando va junto al «?» de las notas: texto, no otro
   círculo, para que no parezca el mismo botón dos veces. */
.page-header__manual {
  margin-left: var(--space-2);
  vertical-align: middle;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--color-text-soft);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.page-header__manual:hover,
.page-header__manual:focus-visible { color: var(--color-text); }

@media (pointer: coarse) {
  /* 36 y no 28: 28 era ya una concesión al dedo y se sigue fallando. No llega a
     44 a propósito — va pegado al título y a esa medida compite con él, que es
     justo lo que la nota de arriba de este componente evita. */
  .page-header__guide { width: 36px; height: 36px; font-size: var(--text-sm); }
}
.page-header__subtitle { font-size: var(--text-sm); color: var(--color-text-soft); margin-top: var(--space-1); }

/* On a phone the action does not squeeze in beside the title — it takes its own
   line and the full width, which is also the easiest thing in the page to hit. */
@media (max-width: 640px) {
  .page-header { gap: var(--space-3); margin-bottom: var(--space-4); }
  .page-header__title { font-size: var(--text-xl); }
  .page-header__actions { width: 100%; }
  .page-header__actions > * { flex: 1 1 auto; }

  /* A segmented control is the one thing up here that is a SET and not a
     button, so it takes a whole row and splits it evenly instead of queueing
     with the actions. Here rather than in each page that uses one: the second
     page to want it would have copied the media query, and the third would have
     copied it slightly differently. */
  .page-header__actions :deep(.segmented) { display: flex; flex: 1 1 100%; }
  .page-header__actions :deep(.segmented__btn) { flex: 1; }

  /* ── Las acciones, ancladas abajo ──
     Solo cuando la pantalla lo pide (`sticky-actions`). Arriba se iban con el
     scroll: en una ficha larga, «Guardar» dejaba de existir en cuanto se
     empezaba a leer. Abajo están siempre y en el mismo sitio en todas las
     pantallas que lo piden, que es lo que evita buscarlos en cada una. */
  .page-header__actions--docked {
    position: fixed;
    left: 0; right: 0; bottom: 0;
    z-index: 40;
    margin: 0;
    padding: var(--space-3) var(--space-4);
    padding-bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
    background: var(--color-surface);
    border-top: 1px solid var(--color-border);
    flex-wrap: nowrap;
  }
  /* Repartidos a partes iguales y con altura de dedo: son dos o tres y no
     compiten por el sitio. */
  .page-header__actions--docked > * { flex: 1 1 0; min-height: 44px; }
}
</style>

<style>
/* La barra vive dentro del armazón, que con el teclado fuera mide solo lo que
   queda por encima (ui-kit utils/viewport.js): se apoya en las teclas sin hacer
   nada. Lo único que sobra entonces es el hueco del indicador de inicio, que el
   teclado tapa. Sin scope: `:global(html.x) .y` scoped se queda en `html.x`. */
@media (max-width: 640px) {
  html.keyboard-open .page-header__actions--docked { padding-bottom: var(--space-3); }
}
</style>
